/* eslint-disable max-lines */

import {WaypointDispatcher} from '../../../hooks/useWaypoints';
import {TrackDispatcher} from '../../../hooks/useTracks';
import {AreaDispatcher} from '../../../hooks/useAreas';
import {
    areaFixedLayerIds,
    trackFixedLayerIds,
    publicTrackFixedLayerIds,
    waypointFixedLayerIds
} from '../getSyncMapboxStyle/fixedLayerIds';
import convertLngLat from '../../mapbox/convertLngLat';
import nearestPointOnLine from '@turf/nearest-point-on-line';

interface OpenPopupOptions {
    feature: mapboxgl.MapboxGeoJSONFeature;
    onPopupChange: (popup: MapPopup) => void;
    onSearchResultChange: (searchResult: SearchResult | undefined) => void;
    waypointDispatcher: WaypointDispatcher;
    trackDispatcher: TrackDispatcher;
    areaDispatcher: AreaDispatcher;
    lngLat: mapboxgl.LngLat;
    onFoldersMembershipChange: (membership: MapFoldersMembership) => void;
    highlight: MapHighlight;
    foldersMembership: MapFoldersMembership;
    mapboxStyle: GaiaMapboxStyle | undefined;
    mapboxLayerId: string;
    onOverlaysChange: SetState<MapOverlays>;
    overlays: MapOverlays;
}

function isWaypointPopupFeature(feature: mapboxgl.MapboxGeoJSONFeature) {
    return (
        feature.layer.id === waypointFixedLayerIds.searchResult.pin ||
        feature.layer.id === waypointFixedLayerIds.remoteUser.pin ||
        feature.layer.id === waypointFixedLayerIds.localUser.pin ||
        feature.layer.id === waypointFixedLayerIds.remoteHighlight.pin ||
        feature.layer.id === waypointFixedLayerIds.localHighlight.pin
    );
}

function openWaypointPopup({
    feature,
    onPopupChange,
    onSearchResultChange,
    waypointDispatcher,
    onFoldersMembershipChange,
    highlight,
    foldersMembership,
    mapboxLayerId,
    onOverlaysChange,
    overlays,
}: OpenPopupOptions) {

    if (feature.layer.id === waypointFixedLayerIds.searchResult.pin) {
        const pointLocalId = waypointDispatcher({
            title: feature.properties!.title,
            type: 'create',
            lngLat: (feature.geometry as any).coordinates,
            isTitleLoaded: true,
        });
        onPopupChange({
            type: 'point',
            pointLocalId,
            mapboxLayerId: waypointFixedLayerIds.localUser.pin,
        });
        onSearchResultChange(undefined);
        onOverlaysChange({...overlays, showWaypoints: true});

    } else if (
        feature.layer.id === waypointFixedLayerIds.localUser.pin ||
        feature.layer.id === waypointFixedLayerIds.localHighlight.pin
    ) {
        onPopupChange({
            type: 'point',
            pointLocalId: feature.properties!.pointLocalId,
            mapboxLayerId
        });

    } else if (
        feature.layer.id === waypointFixedLayerIds.remoteUser.pin ||
        feature.layer.id === waypointFixedLayerIds.remoteHighlight.pin
    ) {
        const waypoint = (feature as any).toJSON() as RemoteWaypoint;

        const pointLocalId = waypointDispatcher({type: 'takeLocal', waypoint});
        onPopupChange({type: 'point', pointLocalId, mapboxLayerId});

        if (
            feature.layer.id === waypointFixedLayerIds.remoteHighlight.pin &&
            highlight &&
            highlight.type === 'folder'
        ) {
            const folder = foldersMembership[highlight.id] || {tracks: {}, points: {}};
            onFoldersMembershipChange({
                ...foldersMembership,
                [highlight.id]: {
                    ...folder,
                    tracks: {
                        ...folder.tracks,
                        [waypoint.properties.track_id || '']: true,
                    },
                    points: {
                        ...folder.points,
                        [waypoint.properties.id || '']: true
                    }
                }
            });
        }
    }
}

function isTrackPopupFeature(feature: mapboxgl.MapboxGeoJSONFeature) {
    return (
        feature.layer.id === trackFixedLayerIds.localUser.casing ||
        feature.layer.id === trackFixedLayerIds.remoteUserTracks.casing ||
        feature.layer.id === trackFixedLayerIds.remoteUserRoutes.casing ||
        feature.layer.id === trackFixedLayerIds.remoteHighlight.casing ||
        feature.layer.id === trackFixedLayerIds.localHighlight.casing ||
        feature.layer.id === publicTrackFixedLayerIds.line
    );
}

function openTrackPopup({
    feature,
    onPopupChange,
    trackDispatcher,
    lngLat,
    onFoldersMembershipChange,
    highlight,
    foldersMembership,
    mapboxLayerId,
}: OpenPopupOptions) {

    if (
        feature.layer.id === trackFixedLayerIds.remoteUserTracks.casing ||
        feature.layer.id === trackFixedLayerIds.remoteUserRoutes.casing ||
        feature.layer.id === trackFixedLayerIds.remoteHighlight.casing ||
        feature.layer.id === publicTrackFixedLayerIds.line
    ) {
        const track = (feature as any).toJSON() as RemoteTrack;

        const trackLocalId = trackDispatcher({type: 'takeLocal', track: {
            ...track,
            properties: {
                ...track.properties,
                isPublicTrack: feature.layer.id === publicTrackFixedLayerIds.line,
            }
        }});
        onPopupChange({type: 'track', trackLocalId, lngLat, mapboxLayerId});

        if (
            feature.layer.id === trackFixedLayerIds.remoteHighlight.casing &&
            highlight &&
            highlight.type === 'folder'
        ) {
            const folder = foldersMembership[highlight.id] || {tracks: {}, points: {}};
            onFoldersMembershipChange({
                ...foldersMembership,
                [highlight.id]: {
                    points: {},
                    ...folder,
                    tracks: {
                        ...folder.tracks,
                        [track.properties.id || '']: true
                    }
                }
            });
        }

    } else if (
        feature.layer.id === trackFixedLayerIds.localUser.casing ||
        feature.layer.id === trackFixedLayerIds.localHighlight.casing
    ) {
        onPopupChange({
            type: 'track',
            trackLocalId: feature.properties!.trackLocalId,
            lngLat,
            mapboxLayerId,
        });
    }
}

function isAreaPopupFeature(feature: mapboxgl.MapboxGeoJSONFeature) {
    return (
        feature.layer.id === areaFixedLayerIds.remoteUser.fill ||
        feature.layer.id === areaFixedLayerIds.localUser.fill ||
        feature.layer.id === areaFixedLayerIds.remoteHighlight.fill ||
        feature.layer.id === areaFixedLayerIds.localHighlight.fill
    );
}

function openAreaPopup({
    feature,
    onPopupChange,
    areaDispatcher,
    lngLat,
    onFoldersMembershipChange,
    highlight,
    foldersMembership,
    mapboxLayerId,
}: OpenPopupOptions) {
    const area = (feature as any).toJSON() as RemoteArea;

    const areaLocalId = areaDispatcher({type: 'takeLocal', area});
    onPopupChange({type: 'area', areaLocalId, lngLat, mapboxLayerId});

    if (
        feature.layer.id === areaFixedLayerIds.remoteHighlight.fill &&
        highlight &&
        highlight.type === 'folder'
    ) {
        const folder = foldersMembership[highlight.id] || {tracks: {}, points: {}};
        onFoldersMembershipChange({
            ...foldersMembership,
            [highlight.id]: {
                points: {},
                ...folder,
                tracks: {
                    ...folder.tracks,
                    [area.properties.id || '']: true
                }
            }
        });
    }

}

function isInteractionSpecPopupFeature(
    feature: mapboxgl.MapboxGeoJSONFeature,
    mapboxStyle: GaiaMapboxStyle | undefined,
) {
    return (
        mapboxStyle &&
        mapboxStyle['interaction-layers'] &&
        mapboxStyle['interaction-layers'].indexOf(feature.layer.id) !== -1
    );
}

function openInteractionSpecPopup({
    feature,
    onPopupChange,
    lngLat,
    mapboxLayerId,
}: OpenPopupOptions) {
    onPopupChange({
        type: 'interactionSpec',
        lngLat,
        feature,
        mapboxLayerId,
    });
}

function isOSMPopupFeature(feature: mapboxgl.MapboxGeoJSONFeature) {
    if (!feature.properties || !feature.properties.osm_id) return false;

    if (feature.geometry.type === 'Polygon') return false;
    if (feature.geometry.type === 'MultiPolygon') return false;

    return true;
}

function openOSMPopup({feature, onPopupChange, lngLat, mapboxLayerId}: OpenPopupOptions) {
    const title = feature.properties && (feature.properties.name ||
        feature.properties.title ||
        `Unnamed ${feature.properties.type || feature.properties.class || 'item'}`);

    onPopupChange({type: 'osm', lngLat, title, osmId: feature.properties!.osm_id, mapboxLayerId});
}

export function isPopupFeature(
    feature: mapboxgl.MapboxGeoJSONFeature,
    mapboxStyle: GaiaMapboxStyle | undefined,
) {
    return (
        isWaypointPopupFeature(feature) ||
        isTrackPopupFeature(feature) ||
        isAreaPopupFeature(feature) ||
        isInteractionSpecPopupFeature(feature, mapboxStyle) ||
        isOSMPopupFeature(feature)
    );
}

export default function openPopup(unsnappedOptions: OpenPopupOptions) {
    const options = {
        ...unsnappedOptions,
        lngLat: snapLngLatToFeature(unsnappedOptions.feature, unsnappedOptions.lngLat)
    };

    if (isWaypointPopupFeature(options.feature)) {
        openWaypointPopup(options);
    } else if (isTrackPopupFeature(options.feature)) {
        openTrackPopup(options);
    } else if (isAreaPopupFeature(options.feature)) {
        openAreaPopup(options);
    } else if (isInteractionSpecPopupFeature(options.feature, options.mapboxStyle)) {
        openInteractionSpecPopup(options);
    } else if (isOSMPopupFeature(options.feature)) {
        openOSMPopup(options);
    }
}

function snapLngLatToFeature(feature: mapboxgl.MapboxGeoJSONFeature, eventLngLat: mapboxgl.LngLat) {
    if (feature.geometry.type === 'Point') {
        return convertLngLat(feature.geometry.coordinates);

    } else if (
        feature.geometry.type === 'LineString' ||
        feature.geometry.type === 'MultiLineString'
    ) {
        const snapped = nearestPointOnLine(feature.geometry, eventLngLat.toArray());
        if (snapped && snapped.geometry) {
            return convertLngLat(snapped.geometry.coordinates);
        } else {
            return eventLngLat;
        }

    } else {
        return eventLngLat;
    }
}
